/**
 * DashboardPage — Homepage Dashboard
 *
 * Architecture (F1–F5, DB-3–DB-6):
 *
 *   F1 — DashboardDataProvider     : fetches shared data once; all widgets consume via context
 *   F2 → DB-3 — DashboardPersonaProvider : persona now derived from platform role (not demo toggle)
 *   F3/F4 — DashboardWidgetPreferencesProvider : widget visibility/order + API persistence
 *   F5 — Shell routing             : switches between Executive, PM, Finance shells
 *
 *   DB-3 : Homepage renders role-appropriate shell based on resolved user role:
 *            Admin/Executive → Executive Dashboard
 *            PM              → PM Dashboard
 *            Project Team    → ProjectTeamHomepage (locked project list)
 *
 *   DB-4 : Project Team homepage renders locked project list (ProjectTeamHomepage component)
 *
 *   DB-5 : Settings cog hidden for Project Team (handled in SettingsCogPanel)
 *
 *   DB-6 : Finance widgets added by PM show "Showing your projects only" scoped indicator
 *
 * The three shells live in /components/dashboard/shells/ and each shell renders
 * its own ordered widget list driven by DashboardWidgetPreferencesContext.
 *
 * PersonaToggle removed from production UI (DB-1, DB-2 — removed from scope).
 */

import React from 'react';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { DashboardDataProvider } from '@/contexts/DashboardDataContext';
import { DashboardPersonaProvider, useDashboardPersona } from '@/contexts/DashboardPersonaContext';
import { DashboardWidgetPreferencesProvider } from '@/contexts/DashboardWidgetPreferencesContext';
import { SettingsCogPanel } from '@/components/dashboard/SettingsCogPanel';
import { ExecutiveDashboardShell } from '@/components/dashboard/shells/ExecutiveDashboardShell';
import { ProjectManagerDashboardShell } from '@/components/dashboard/shells/ProjectManagerDashboardShell';
import { ProjectTeamHomepage } from '@/components/dashboard/ProjectTeamHomepage';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/providers/AuthProvider';
import type { DashboardPersona } from '@/contexts/DashboardPersonaContext';
import { WelcomeModal } from '@/components/ProductTour/WelcomeModal';

// ── Greeting helper ───────────────────────────────────────────────────────────

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

// ── Error boundary ────────────────────────────────────────────────────────────

interface ErrorBoundaryState { error: Error | null }
class ShellErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm">
          <p className="font-semibold text-destructive mb-2">Dashboard render error</p>
          <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-all">{this.state.error.message}{'\n\n'}{this.state.error.stack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Shell subtitle map ────────────────────────────────────────────────────────

const PERSONA_SUBTITLES: Record<DashboardPersona, string> = {
  executive:       'Portfolio health, profitability, and risk overview',
  project_manager: 'Your projects, delivery health, and team status',
  project_team:    'Your allocated projects',
};

// ── Top-level export ──────────────────────────────────────────────────────────

export function DashboardPage({ onNavigate }: { onNavigate?: (page: string) => void } = {}) {
  return (
    <DashboardDataProvider>
      <DashboardPersonaProvider>
        <DashboardPreferencesWrapper onNavigate={onNavigate} />
      </DashboardPersonaProvider>
    </DashboardDataProvider>
  );
}

// ── Preferences wrapper ───────────────────────────────────────────────────────
// Reads the active persona first (requires DashboardPersonaProvider in tree),
// then passes it into DashboardWidgetPreferencesProvider so preferences are
// scoped to the active persona.

function DashboardPreferencesWrapper({ onNavigate }: { onNavigate?: (page: string) => void }) {
  const { persona, isLoading } = useDashboardPersona();

  if (isLoading) {
    return <DashboardLoadingSkeleton />;
  }

  return (
    <DashboardWidgetPreferencesProvider persona={persona}>
      <DashboardContent persona={persona} onNavigate={onNavigate} />
    </DashboardWidgetPreferencesProvider>
  );
}

// ── Content ───────────────────────────────────────────────────────────────────
// Renders the page header (shared across all personas) and then routes to the
// correct persona shell based on the user's resolved platform role (DB-3).

function DashboardContent({
  persona,
  onNavigate,
}: {
  persona: DashboardPersona;
  onNavigate?: (page: string) => void;
}) {
  const { authenticatedUser } = useAuth();
  const firstName = authenticatedUser?.name?.split(' ')[0] ?? null;
  const title = firstName ? `${getGreeting()}, ${firstName}` : getGreeting();
  const subtitle = PERSONA_SUBTITLES[persona];

  // DB-5: Settings cog is hidden for project_team (handled inside SettingsCogPanel)
  // DB-3: PersonaToggle removed — role drives the shell, not a manual toggle
  const headerActions = (
    <div className="flex items-center gap-2" data-tour="dashboard-settings-cog">
      <SettingsCogPanel />
    </div>
  );

  // Select the tour ID appropriate for the current persona.
  // Project team users have a simpler homepage so we skip the tour for them.
  const tourId = persona === 'executive' ? 'dashboard-executive' : 'dashboard-pm';
  const showWelcomeModal = persona !== 'project_team';

  return (
    <div className="space-y-6">
      {/* Welcome modal — auto-shows once for first-time users (not shown for project_team) */}
      {showWelcomeModal && <WelcomeModal tourId={tourId} />}

      {/* Shared page header — role-aware greeting + settings cog (hidden for Project Team) */}
      <div data-tour="dashboard-header">
        <PageHeaderBlock
          title={title}
          subtitle={subtitle}
          size={20}
          showActions
          actions={headerActions}
        />
      </div>

      {/* DB-3: Route to the correct shell based on resolved user role */}

      {/* Admin + Executive → Executive Dashboard */}
      {persona === 'executive' && (
        <ExecutiveDashboardShell onNavigate={onNavigate} />
      )}

      {/* Project Manager → PM Dashboard
          DB-6: PM Finance widgets show "Showing your projects only" scoped indicator.
          The showFinanceWidgetScopeIndicator prop signals the shell to render the
          scope badge on any Finance cross-persona widgets added by the PM. */}
      {persona === 'project_manager' && (
        <ProjectManagerDashboardShell onNavigate={onNavigate} showFinanceWidgetScopeIndicator />
      )}

      {/* Project Team → Locked project list (DB-4) */}
      {persona === 'project_team' && (
        <ProjectTeamHomepage onNavigate={onNavigate} />
      )}
    </div>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────
// Shown while backend permissions are being fetched so the dashboard doesn't
// flash the wrong persona shell before the real permissions resolve.

function DashboardLoadingSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header skeleton */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
      </div>
      {/* KPI tiles row */}
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28 rounded-lg" />
        ))}
      </div>
      {/* Widget rows */}
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-64 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-48 rounded-lg" />
        <Skeleton className="h-48 rounded-lg" />
      </div>
    </div>
  );
}
